import { type ComponentProps } from "react";
import { Link, useLocation } from "react-router";
import {
  BookOpen,
  Building2,
  Cable,
  CalendarDays,
  ChevronLeft,
  CreditCard,
  FileText,
  FolderOpen,
  Handshake,
  IdCard,
  LayoutDashboard,
  Plug,
  Plus,
  ScrollText,
  Settings,
  Users,
} from "lucide-react";
import { BrandIcon } from "@/components/shell/BrandIcon";
import { kbMaterialsPath, kbWorkbenchPath } from "@/lib/kb-routes";
import { ContextHeader } from "./ContextHeader";
import { NavMain, type NavGroup } from "./NavMain";
import { NavUser } from "./NavUser";
import { ThemeToggle } from "./ThemeToggle";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { defaultAppSection, enabledBrandModules, findModule } from "@/lib/modules";
import { brandOperatingToolPath, brandSitePath, isBrandSiteSection, productPanelBackLabel, productPanelBackPath } from "@/lib/brand-site-routing";
import { productSettingsChildren, productSettingsSectionTitle } from "@/lib/product-settings-nav";
import { cn } from "@/lib/utils";
import { useShellRouteInfo } from "@/lib/shell-navigation";

export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const { setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  const workspace = useOptionalAccountWorkspace();
  const shell = useShellRouteInfo();
  const accountId = shell.accountId || undefined;
  const base = accountId ? `/accounts/${accountId}` : "";
  const brands = workspace?.brands ?? [];
  const isAuthorizedAccess = workspace?.accessKind === "authorization";
  const openCreateBrand = workspace?.openCreateBrand;

  if (!accountId) {
    return (
      <Sidebar variant="inset" {...props}>
        <SidebarHeader>
          <ContextHeader level="account" />
        </SidebarHeader>
        <SidebarContent />
        <SidebarFooter className="gap-2">
          <div className="flex items-center gap-1 px-2">
            <div className="min-w-0 flex-1">
              <NavUser />
            </div>
            <ThemeToggle />
          </div>
        </SidebarFooter>
      </Sidebar>
    );
  }

  const productApp =
    shell.mode === "product" ? findModule(workspace?.modules ?? [], shell.appKey ?? undefined) : null;

  // Slide stack: account (0) → brand (1) → tool/app (2)
  const showToolPanel = shell.mode === "product" && Boolean(shell.brandId);
  const showBrandPanel = shell.mode === "brand" && Boolean(shell.brandId);
  const panelDepth = showToolPanel ? 2 : showBrandPanel ? 1 : 0;
  const contextLevel = showToolPanel ? "app" : showBrandPanel ? "brand" : "account";
  const activeBrand =
    brands.find((brand) => String(brand.id) === shell.brandId) ?? null;
  const enterpriseScope = { accountId };
  const brandScope = shell.brandId ? { accountId, brandId: shell.brandId } : null;
  const toolTitle = productApp?.name ?? shell.appName ?? "工具";
  const toolNav = productApp?.nav ?? [];

  const brandNavGroups: NavGroup[] =
    brandScope != null && shell.brandId != null
      ? [
          {
            label: "品牌",
            items: [
              {
                title: "画像",
                url: `${base}/brands/${shell.brandId}`,
                icon: IdCard,
              },
              {
                title: "运营计划",
                url: `${base}/brands/${shell.brandId}/plan`,
                icon: CalendarDays,
              },
              {
                title: "素材库",
                url: kbMaterialsPath(brandScope),
                icon: FolderOpen,
              },
              {
                title: "知识库",
                url: kbWorkbenchPath(brandScope),
                icon: BookOpen,
              },
            ],
          },
        ]
      : [];

  const brandSettingsGroups: NavGroup[] =
    brandScope != null && shell.brandId != null
      ? [
          {
            label: "品牌设置",
            items: [
              {
                title: "基本信息",
                url: `${base}/brands/${shell.brandId}/settings/basic`,
                icon: Settings,
              },
              {
                title: "成员权限",
                url: `${base}/brands/${shell.brandId}/settings/members`,
                icon: Users,
              },
              {
                title: "连接器(MCP)",
                url: `${base}/brands/${shell.brandId}/connectors`,
                icon: Cable,
              },
            ],
          },
        ]
      : [];

  // 运营工具：仅本品牌已开通的（账户订阅 ≠ 品牌开通）
  const brandToolItems =
    activeBrand != null
      ? enabledBrandModules(workspace?.modules ?? [], activeBrand).map((app) => ({
          title: app.name,
          url: brandOperatingToolPath(
            accountId ?? "",
            shell.brandId!,
            app.key,
            defaultAppSection(app),
          ),
          icon: LayoutDashboard,
        }))
      : [];

  // 产品设置子项：有配置时单独成「{产品}设置」大区（对齐账户「账户管理」）
  const productSettingsItems = productSettingsChildren(shell.appKey);
  const productSettingsLabel = productSettingsSectionTitle(shell.appKey, toolTitle);
  const workSurfaceNav = toolNav.filter((item) => {
    if (shell.appKey === "brand_site" && !shell.brandSiteId && item.key !== "overview") {
      return false;
    }
    if (item.key !== "settings") return true;
    return productSettingsItems.length === 0;
  });

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <ContextHeader
          level={contextLevel}
          brand={activeBrand}
          appTitle={showToolPanel ? toolTitle : null}
        />
      </SidebarHeader>

      <SidebarContent className="overflow-hidden">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            className={cn(
              "flex w-[300%] flex-1 transition-transform duration-300 ease-out",
              panelDepth === 2 && "-translate-x-2/3",
              panelDepth === 1 && "-translate-x-1/3",
              panelDepth === 0 && "translate-x-0",
            )}
          >
            {/* Account menu panel */}
            <div className="flex w-1/3 min-w-0 flex-col overflow-y-auto">
              <SidebarGroup>
                <SidebarGroupLabel>企业</SidebarGroupLabel>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={shell.mode === "enterprise"}
                      tooltip="画像"
                    >
                      <Link to={base} onClick={() => setOpenMobile(false)}>
                        <Building2 />
                        <span>画像</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === `${base}/materials`}
                      tooltip="素材库"
                    >
                      <Link
                        to={kbMaterialsPath(enterpriseScope)}
                        onClick={() => setOpenMobile(false)}
                      >
                        <FolderOpen />
                        <span>素材库</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={
                        shell.mode === "account-admin" &&
                        (pathname === `${base}/kb` || pathname.startsWith(`${base}/kb/`))
                      }
                      tooltip="知识库"
                    >
                      <Link
                        to={kbWorkbenchPath(enterpriseScope)}
                        onClick={() => setOpenMobile(false)}
                      >
                        <BookOpen />
                        <span>知识库</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>

              <SidebarGroup>
                <SidebarGroupLabel>品牌</SidebarGroupLabel>
                {!isAuthorizedAccess && openCreateBrand ? (
                  <SidebarGroupAction
                    type="button"
                    title="添加品牌"
                    aria-label="添加品牌"
                    className="text-sidebar-foreground/40 opacity-50 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:opacity-100"
                    onClick={() => {
                      setOpenMobile(false);
                      openCreateBrand();
                    }}
                  >
                    <Plus />
                  </SidebarGroupAction>
                ) : null}
                <SidebarMenu>
                  {brands.map((brand) => (
                    <SidebarMenuItem key={brand.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={shell.brandId === String(brand.id)}
                        tooltip={brand.name}
                      >
                        <Link
                          to={`${base}/brands/${brand.id}`}
                          onClick={() => setOpenMobile(false)}
                        >
                          <BrandIcon
                            name={brand.name}
                            iconKey={brand.icon_key}
                            color={brand.color}
                            size="sm"
                          />
                          <span className="font-heading">{brand.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}

                  {brands.length === 0 && (
                    <SidebarMenuItem>
                      <SidebarMenuButton className="pointer-events-none opacity-60">
                        <BrandIcon name="品" size="sm" />
                        <span>暂无品牌</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroup>

              {!isAuthorizedAccess && (
                <NavMain
                  groups={
                    [
                      {
                        label: "账户管理",
                        items: [
                          { title: "账户设置", url: `${base}/settings`, icon: Settings },
                          { title: "成员", url: `${base}/members`, icon: Users },
                          { title: "订阅", url: `${base}/subscriptions`, icon: CreditCard },
                          { title: "渠道", url: `${base}/channels`, icon: Plug },
                          {
                            title: "代管授权",
                            url: `${base}/authorizations`,
                            icon: Handshake,
                            alsoActiveWhen: (path) =>
                              path.includes("/received-authorizations/") ||
                              path.includes("/authorizations"),
                          },
                          { title: "审计日志", url: `${base}/audit-log`, icon: ScrollText },
                        ],
                      },
                    ] satisfies NavGroup[]
                  }
                />
              )}
            </div>

            {/* Brand menu panel — slides in from the right */}
            <div className="flex w-1/3 min-w-0 flex-col overflow-y-auto">
              <SidebarGroup>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="返回账户">
                      <Link to={base} onClick={() => setOpenMobile(false)}>
                        <ChevronLeft />
                        <span>返回账户</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>

              {brandNavGroups.length > 0 ? <NavMain groups={brandNavGroups} /> : null}

              {brandToolItems.length > 0 ? (
                <NavMain
                  groups={[
                    {
                      label: "运营工具",
                      items: brandToolItems,
                    },
                  ]}
                />
              ) : null}

              {brandSettingsGroups.length > 0 ? <NavMain groups={brandSettingsGroups} /> : null}
            </div>

            {/* Tool / product-app panel — slides in from the right */}
            <div className="flex w-1/3 min-w-0 flex-col overflow-y-auto">
              <SidebarGroup>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip={productPanelBackLabel(shell.appKey)}>
                      <Link
                        to={productPanelBackPath(accountId ?? "", shell.brandId!, shell.appKey)}
                        onClick={() => setOpenMobile(false)}
                      >
                        <ChevronLeft />
                        <span>{productPanelBackLabel(shell.appKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
                <SidebarGroupLabel className="font-heading truncate">
                  {toolTitle}
                </SidebarGroupLabel>
                <SidebarMenu>
                  {workSurfaceNav.map((item) => {
                    const Icon =
                      item.key === "overview"
                        ? LayoutDashboard
                        : item.key === "settings"
                          ? Settings
                          : item.key === "materials"
                            ? FolderOpen
                            : FileText;
                    const url =
                      item.key === "materials" && shell.brandId && shell.brandSiteId
                        ? kbMaterialsPath({
                            accountId: accountId ?? "",
                            brandId: shell.brandId,
                            brandSiteId: shell.brandSiteId,
                          })
                        : shell.appKey === "brand_site" &&
                            shell.brandSiteId &&
                            isBrandSiteSection(item.key)
                          ? brandSitePath(
                              accountId ?? "",
                              shell.brandId!,
                              shell.brandSiteId,
                              item.key,
                            )
                          : `${base}/brands/${shell.brandId}/apps/${shell.appKey}/${item.key}`;
                    const active = shell.appSection === item.key;

                    return (
                      <SidebarMenuItem key={item.key}>
                        <SidebarMenuButton asChild isActive={active} tooltip={item.title}>
                          <Link to={url} onClick={() => setOpenMobile(false)}>
                            <Icon />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>

              {/* 产品设置：对齐账户「账户管理」—— GroupLabel + 平级链接，不用 Sub 缩进树 */}
              {productSettingsItems.length > 0 ? (
                <SidebarGroup>
                  <SidebarGroupLabel>{productSettingsLabel}</SidebarGroupLabel>
                  <SidebarMenu>
                    {productSettingsItems.map((child) => {
                      const childUrl = `${base}/brands/${shell.brandId}/apps/${shell.appKey}/settings/${child.key}`;
                      const childActive =
                        shell.appSection === "settings" && shell.appSubsection === child.key;
                      return (
                        <SidebarMenuItem key={child.key}>
                          <SidebarMenuButton
                            asChild
                            isActive={childActive}
                            tooltip={child.title}
                          >
                            <Link to={childUrl} onClick={() => setOpenMobile(false)}>
                              {child.key === "members" ? <Users /> : <FileText />}
                              <span>{child.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroup>
              ) : null}
            </div>
          </div>
        </div>
      </SidebarContent>

      <SidebarFooter className="gap-2">
        <div className="flex items-center gap-1 px-2">
          <div className="min-w-0 flex-1">
            <NavUser />
          </div>
          <ThemeToggle />
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
